<template>
  <div>
    <v-row>
      <v-col cols="12">
        <customer-select
          v-model="form.person_id"
          item-text="name"
          item-value="id"
          :loading="loading"
          :mass-market="resource.mass_market"
          :building-complex-id="resource.building_complex_id"
          :customer="customer"
          :rules="getRulesByName('person_id')"
          :label="`${$t('BOOKING.RESERVATIONS.CUSTOMER')} *`"
          @change="updateValue('person_id', $event)"
        />
      </v-col>
    </v-row>
    <v-form
      ref="validRequestForm"
      v-model="validRequestForm"
      @input="$emit('validRequestForm', $event)"
    >
      <v-row>
        <v-col>
          <v-text-field
            v-model.number="form.number_of_visitors"
            name="number_of_visitors"
            :readonly="readonly || loading"
            :loading="loading"
            :disabled="isIndividual"
            :rules="getRulesByName('number_of_visitors')"
            :error-messages="errors.number_of_visitors"
            type="number"
            step="1"
            min="1"
            :max="resource.capacity"
            prepend-inner-icon="mdi-account-multiple-outline"
            @input="updateValue('number_of_visitors', $event)"
          ></v-text-field>
        </v-col>
        <v-col>
          <duration
            v-model="form.duration"
            :rules="getRulesByName('duration')"
            :error-messages="errors.duration"
            :allowed-hours="hoursRange"
            :allowed-minutes="minutesRange"
            append-icon="mdi-clock-time-four-outline"
            hide-days
            hide-seconds
            hide-not-allowed-options
            @input="updateValue('duration', $event)"
          />
        </v-col>
      </v-row>
    </v-form>
    <v-row>
      <v-col cols="12">
        <booking-resource-sheet
          class="pa-4"
          :resource="resource"
          rounded
          small
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-textarea
          v-model="form.info"
          name="info"
          :rows="4"
          :counter="400"
          :label="$t('ITEM.ADDITIONAL_INFO')"
          :readonly="readonly"
          :rules="getRulesByName('info')"
          :error-messages="errors.info"
          outlined
          @input="updateValue('info', $event)"
        ></v-textarea>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { dateFormat } from '@/modules/_global/filters';
import {
  maxLength,
  notEmpty,
  isNumeric,
  isInteger,
  positiveNumber,
  minNumeric
} from '@/modules/_global/variables/rules';
import { inRange } from 'lodash';
import formEditable from '@/modules/_global/mixins/form-editable';
import BookingResourceSheet from '../BookingResourceSheet.vue';
import CustomerSelect from '@/modules/booking_reservations/components/CustomerSelect';
import Duration from '@/modules/_global/_components/duration/Duration';
import { HOUR, MINUTE } from '@/modules/booking_resources/common/types';
import { INDIVIDUAL } from '@/modules/booking_resources/common/capacity';

export default {
  name: 'BookingReservationCreateForm',

  filters: {
    dateFormat
  },

  components: {
    Duration,
    BookingResourceSheet,
    CustomerSelect
  },

  mixins: [formEditable],

  props: {
    resource: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      validRequestForm: false,
      form: {
        person_id: null,
        created_at: null,
        duration: 0,
        number_of_visitors: null,
        person: {
          full_name: null
        }
      },
      customer: null
    };
  },

  computed: {
    isIndividual() {
      return this.form.capacity_type === INDIVIDUAL;
    },
    rules() {
      return {
        person_id: [notEmpty],
        duration: [notEmpty, isNumeric],
        number_of_visitors: [
          notEmpty,
          isNumeric,
          positiveNumber,
          isInteger,
          minNumeric(1)
        ],
        info: [maxLength(400)]
      };
    },
    ...mapGetters(['loading'])
  },

  mounted() {
    this.$nextTick(() => {
      this.customer = (this.form.person && this.form.person.full_name) || null;
    });
  },

  methods: {
    minutesRange(v) {
      if (this.resource.type_id === MINUTE) {
        return (
          inRange(
            v,
            this.resource.reserve_max > 60 ? 0 : this.resource.reserve_min,
            this.resource.reserve_max + this.resource.step
          ) && v % this.resource.step === 0
        );
      } else if (this.resource.type_id === HOUR) {
        return v % this.resource.step === 0;
      }
      return true;
    },

    hoursRange(v) {
      if (this.resource.type_id === MINUTE) {
        if (this.resource.reserve_max < 60) {
          return false;
        }

        if (this.resource.reserve_max >= 60) {
          return v <= Math.floor(this.resource.reserve_max / 60);
        }
      }
      if (this.resource.type_id === HOUR) {
        return v <= this.resource.reserve_max;
      }
    }
  }
};
</script>
